import React from 'react';

import {
    CreatePaperPlanInput,
    CreatePaperPlanOutput,
    CreateWeightsProfilesInput
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';

import { isEqual } from 'lodash';
import { useLocation } from 'react-router';
import { Route, Switch, useHistory } from 'react-router-dom';
import { contractConfigurationClient } from 'src/api/client';
import { BreadcrumbWrapper } from 'src/components/Breadcrumb';
import { ConfirmationModal } from 'src/components/ConfirmationModal';
import { StepTracker } from 'src/components/StepTracker';
import { DEFAULT_TOASTER_TIMEOUT, PlanWeightsTab } from 'src/constants';
import { defaultPlanValues } from 'src/constants/paperPlans';
import { ROUTES } from 'src/constants/routes';
import { AppContext } from 'src/contexts/appContext';
import { IBreadcrumb } from 'src/types/IBreadcrumb';
import { IFirstPartyCorePlan, IMultiplierType } from 'src/types/IRateCard';
import { IStepTrackerStep } from 'src/types/IStepTrackerStep';
import { IToast } from 'src/types/IToaster';
import { IWeightsProfile } from 'src/types/IWeightsProfile';
import {
    getCorePlan,
    getPaperPlanAttributesInput,
    getPlanDiscountRate,
    getPlanGroup,
    getWeightsProfilesInput
} from 'src/util/PaperPlans/PaperPlanUtils';
import { getDerivedMultiplierName } from 'src/util/RateCards';
import { getDateAndTimestamp } from 'src/util/dateTimeUtil';

import './AddPaperPlan.scss';
import { DEFAULT_PLAN_WEIGHTS_MAP } from './AddWeights/addWeightsUtils';
import { PlanDetails } from './PlanDetails';
import { PlanReview } from './PlanReview';
import { PlanSettings } from './PlanSettings';

interface IAddPaperPlanProps {
    corePlanList: IFirstPartyCorePlan[];
    multiplierTypeList: IMultiplierType[];
}

export const AddPaperPlan = ({ corePlanList, multiplierTypeList }: IAddPaperPlanProps) => {
    const history = useHistory();

    const breadcrumbItems: IBreadcrumb[] = [
        { link: ROUTES.PAPER_PLANS.INDEX, title: 'PAPER plans' },
        { link: '', title: 'Add plan' }
    ];

    const steps: IStepTrackerStep[] = [
        {
            stepId: ROUTES.PAPER_PLANS.STEP.SETTINGS,
            stepTitle: 'Plan settings',
            StepComponent: PlanSettings
        },
        {
            stepId: ROUTES.PAPER_PLANS.STEP.DETAILS,
            stepTitle: 'Plan details',
            StepComponent: PlanDetails
        },
        {
            stepId: ROUTES.PAPER_PLANS.STEP.REVIEW,
            stepTitle: 'Review and add',
            StepComponent: PlanReview
        }
    ];

    const ADD_PAPER_PLAN_BASE_PATH = `${ROUTES.PAPER_PLANS.INDEX}${ROUTES.PAPER_PLANS.ADD}`;

    const { pathname } = useLocation();
    const step = `/${pathname.split('/').pop()}`;
    const currentStepIndex = steps.findIndex(({ stepId }) => stepId === step);

    const { setToasts, userInfo, setTopAlertMsg } = React.useContext(AppContext);
    const [isCancelModalOpen, setIsCancelModalOpen] = React.useState<boolean>(false);
    const [planValues, setPlanValues] = React.useState(defaultPlanValues);
    const [planWeights, setPlanWeights] = React.useState(DEFAULT_PLAN_WEIGHTS_MAP);

    const [isBundlePriceReminderModalOpen, setIsBundlePriceReminderModalOpen] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState(false);

    const handlePlanValuesChange = (key: string, value: string | boolean) => {
        setPlanValues((prevState) => ({
            ...prevState,
            [key]: value
        }));
    };

    const handlePlanWeightsChange = (vendor: PlanWeightsTab, key: string, value: string) => {
        setPlanWeights((previousMap) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const vendorWeights = previousMap.get(vendor)!;
            const newVendorWeights = {
                ...vendorWeights,
                [key]: value
            };

            return new Map(previousMap.set(vendor, newVendorWeights));
        });
    };

    const handlePlanWeightsBusinessRulesChange = (planWeights: Map<PlanWeightsTab, IWeightsProfile>) => {
        setPlanWeights(planWeights);
    };

    const onNext = () => {
        history.push(`${ADD_PAPER_PLAN_BASE_PATH}${steps[currentStepIndex + 1].stepId}`);
    };

    const onPrevious = () => {
        history.push(`${ADD_PAPER_PLAN_BASE_PATH}${steps[currentStepIndex - 1].stepId}`);
    };

    const getPaperPlanInput = (isDraftPlan: boolean) => {
        const planStartDate = getDateAndTimestamp(planValues.startDate)[0];
        const planEndDate = getDateAndTimestamp(planValues.endDate)[0];
        const somePlanObj = {
            renewalCadence: planValues.renewalCadence,
            planGroup: getPlanGroup(planValues),
            promoDurationInDays: planValues.promoDurationInDays
        } as Record<string, string | boolean | number>;
        const derivedMultiplierName: string = getDerivedMultiplierName(somePlanObj);
        const corePlanDisplayName: string = getCorePlan(planValues);
        return {
            paperPlanName: planValues.paperPlanName,
            displayName: planValues.displayName,
            description: planValues.description,
            territory: planValues.territory,
            startDate: planStartDate,
            endDate: planEndDate || undefined,
            isDraft: isDraftPlan,
            createdBy: userInfo.username,
            entityTypeId: 1,
            attributes: getPaperPlanAttributesInput(planValues),
            ...(!isDraftPlan && {
                planDiscountRate: getPlanDiscountRate(
                    derivedMultiplierName,
                    multiplierTypeList,
                    corePlanDisplayName,
                    corePlanList
                )
            })
        } as CreatePaperPlanInput;
    };

    const onSaveDraft = () => {
        const paperPlanInput = getPaperPlanInput(true);
        contractConfigurationClient.createPaperPlan(CreatePaperPlanInput.clone(paperPlanInput)).subscribe(
            () => {
                const toast: IToast = {
                    id: 'save-draft-plan-toast',
                    timeout: DEFAULT_TOASTER_TIMEOUT,
                    message: '1 new plan saved as draft'
                };
                setToasts([toast]);
                history.push(ROUTES.PAPER_PLANS.INDEX);
            },
            (error: UnknownCoralError) => {
                // eslint-disable-next-line no-console
                console.error('createPaperPlan unexpected error', error.receivedException.message);
                setTopAlertMsg(error.receivedException.message);
            }
        );
    };

    const onSubmit = () => {
        setIsLoading(true);
        const paperPlanInput = getPaperPlanInput(false);
        contractConfigurationClient
            .createPaperPlan(CreatePaperPlanInput.clone(paperPlanInput))
            .toPromise()
            .then((response: CreatePaperPlanOutput) => {
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                const createdPlanIds = response.paperPlanIds!;
                const planWeightsInput = getWeightsProfilesInput(userInfo.username, planWeights, createdPlanIds);
                return contractConfigurationClient
                    .createWeightsProfiles(CreateWeightsProfilesInput.clone(planWeightsInput))
                    .toPromise();
            })
            .then(() => {
                const toast: IToast = {
                    id: 'add-plan-toast',
                    timeout: DEFAULT_TOASTER_TIMEOUT,
                    message: '1 new plan added'
                };
                setToasts([toast]);
                history.push(ROUTES.PAPER_PLANS.INDEX);
            })
            .catch((error: UnknownCoralError) => setTopAlertMsg(error.receivedException.message))
            .finally(() => {
                setIsBundlePriceReminderModalOpen(false);
                setIsLoading(false);
            });
    };

    const onCancel = () => {
        if (!isEqual(planValues, defaultPlanValues)) {
            setIsCancelModalOpen(true);
        } else {
            history.push(ROUTES.PAPER_PLANS.INDEX);
        }
    };

    const handleBreadcrumbRedirect = () => {
        if (!isEqual(planValues, defaultPlanValues)) {
            setIsCancelModalOpen(true);
            return true;
        } else {
            return false;
        }
    };

    return (
        <Column alignmentHorizontal="center" spacingInset="500 500 600">
            <Column width={950} maxWidth="100%" spacing="500">
                <BreadcrumbWrapper breadcrumbItems={breadcrumbItems} handleRedirect={handleBreadcrumbRedirect} />

                <Row alignmentVertical="top" wrap="down" widths={['fit', 'fill']}>
                    <Column minWidth="220px" alignmentHorizontal="start" spacing="200">
                        <StepTracker
                            steps={steps}
                            basePath={ADD_PAPER_PLAN_BASE_PATH}
                            currentStepIndex={currentStepIndex}
                        />
                    </Column>
                    <Column spacing="300" maxWidth="550px">
                        <Switch>
                            {steps.map(({ stepId, StepComponent }) => (
                                <Route key={stepId} path={`${ADD_PAPER_PLAN_BASE_PATH}${stepId}`}>
                                    <StepComponent
                                        planValues={planValues}
                                        planWeights={planWeights}
                                        setPlanValues={setPlanValues}
                                        handlePlanValuesChange={handlePlanValuesChange}
                                        handlePlanWeightsChange={handlePlanWeightsChange}
                                        handlePlanWeightsBusinessRulesChange={handlePlanWeightsBusinessRulesChange}
                                        steps={steps}
                                        onCancel={onCancel}
                                        onPrevious={onPrevious}
                                        onNext={onNext}
                                        onSaveDraft={onSaveDraft}
                                        onSubmit={() => setIsBundlePriceReminderModalOpen(true)}
                                    />
                                </Route>
                            ))}
                        </Switch>
                    </Column>
                </Row>
            </Column>

            <ConfirmationModal
                isOpen={isCancelModalOpen}
                onClose={() => setIsCancelModalOpen(false)}
                title="Cancel add plan"
                body="Are you sure you want to cancel? Any changes will be lost."
                primaryButtonText="Cancel add plan"
                primaryButtonOnClick={() => {
                    history.push(ROUTES.PAPER_PLANS.INDEX);
                }}
                secondaryButtonText="Go back"
                secondaryButtonOnClick={() => setIsCancelModalOpen(false)}
            />

            <ConfirmationModal
                isOpen={isBundlePriceReminderModalOpen}
                onClose={() => setIsBundlePriceReminderModalOpen(false)}
                title="Add plan"
                body="Please remember to visit the bundle prices screen to create prices if necessary."
                primaryButtonText="Yes, add plan"
                primaryButtonOnClick={onSubmit}
                secondaryButtonText="No, go back"
                secondaryButtonOnClick={() => setIsBundlePriceReminderModalOpen(false)}
                isLoading={isLoading}
            />
        </Column>
    );
};
